import { create } from "zustand";
import { brandApi } from "../services/brand-api";
import type { Brand, BrandLogoResponse, BrandRequest, BrandResponse, BrandState, BrandUpdateRequest } from "../types";

export const useBrandStore = create<BrandState>((set) => ({
	selectedBrand: null,
	brands: null,

	index: async () => {
		const result = await brandApi.index();

		if (result.success && result.data) {
			set({ brands: result.data });
			return result.data;
		}

		return null;
	},

	view: async (id: string) => {
		const result = await brandApi.view(id);

		if (result.success && result.data) {
			// Update the brand in the brands array, preserving the role from the existing brand
			// (the view API may not return role since it's membership data, not workspace data)
			set((state) => {
				const existingBrand = state.brands?.find((b) => b.id === id);
				const updatedBrand = {
					...result.data!,
					// Preserve role from existing brand if not in API response
					role: result.data!.role || existingBrand?.role || "editor",
				};
				return {
					selectedBrand: updatedBrand,
					brands: state.brands?.map((brand) => (brand.id === id ? updatedBrand : brand)) ?? null,
				};
			});
			return result.data;
		}

		return null;
	},

	create: async (request: BrandRequest): Promise<BrandResponse | null> => {
		const result = await brandApi.create(request);
		if (result.success && result.data) {
			// Add the new brand to the brands array so guards and other components have immediate access
			// Convert BrandResponse to Brand by adding the required fields and handling nullable differences
			const newBrand: Brand = {
				...result.data,
				website_url: result.data.website_url ?? "",
				timezone: result.data.timezone ?? "UTC",
				status: result.data.status ?? "active",
				role: "admin", // Creator is always the admin
				accepted_at: new Date().toISOString(),
			};
			set((state) => ({
				brands: state.brands ? [...state.brands, newBrand] : [newBrand],
			}));
			return result.data;
		}
		return null;
	},

	update: async (id: string, request: BrandUpdateRequest) => {
		const result = await brandApi.update(id, request);

		if (result.success && result.data) {
			return result;
		}

		return null;
	},

	delete: async (id: string) => {
		const result = await brandApi.delete(id);

		if (result.success) {
			return result;
		}

		return null;
	},

	setSelectedBrand: (brand: Brand) => {
		set({ selectedBrand: brand });
	},

	checkSlugAvailability: async (slug: string) => {
		const result = await brandApi.checkSlugAvailability(slug);
		if (result.success && result.data) {
			return result.data.available;
		}
		return false;
	},

	uploadLogo: async (workspaceId: string, file: File): Promise<BrandLogoResponse | null> => {
		const result = await brandApi.uploadLogo(workspaceId, file);
		if (result.success && result.data) {
			// Update the brand's logo_url in the brands array and selectedBrand
			set((state) => ({
				brands: state.brands?.map((brand) => (brand.id === workspaceId ? { ...brand, logo_url: result.data!.logo_url } : brand)) ?? null,
				selectedBrand: state.selectedBrand?.id === workspaceId ? { ...state.selectedBrand, logo_url: result.data!.logo_url } : state.selectedBrand,
			}));
			return result.data;
		}
		return null;
	},

	deleteLogo: async (workspaceId: string): Promise<BrandLogoResponse | null> => {
		const result = await brandApi.deleteLogo(workspaceId);
		if (result.success && result.data) {
			// Update the brand's logo_url to null in the brands array and selectedBrand
			set((state) => ({
				brands: state.brands?.map((brand) => (brand.id === workspaceId ? { ...brand, logo_url: null } : brand)) ?? null,
				selectedBrand: state.selectedBrand?.id === workspaceId ? { ...state.selectedBrand, logo_url: null } : state.selectedBrand,
			}));
			return result.data;
		}
		return null;
	},
}));
